/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  IonCard,
  IonCardContent,
  IonAvatar,
  IonButton,
  IonIcon,
  IonText,
} from "@ionic/react";
import {
  ellipsisVerticalOutline,
  eyeOutline,
  personCircleOutline,
  personRemoveOutline,
  pencilOutline,
  trashBinOutline,
  eyeOffOutline,
  people,
} from "ionicons/icons";
import { MOODS, PostCardProps, PostData, ReactionType } from "../../types/socialJournal";
import PostEngagement from "./PostEngagement";
import BiWazeTherapyCard from "../therapy/BiWazeTherapyCard";
import "./PostCard.css";
import AuthenticatedImage from "./AuthenticatedImage";
import OptionsMenu from "../../pages/Journal/MenuList";
import { resolveProfileDisplay } from "../../utils/helpers";
import Avatar from "../../components/ui/Avatar";
interface MenuItem {
  id: string;
  label: string;
  icon: string;
  color?: string;
  onClick: () => void;
}

interface ExtendedPostCardProps extends PostCardProps {
  userAvatar: string;
  /** Backs the label and avatar letter when the post has no userName. */
  authorEmail?: string;
  /** Backs the label only - a digit is not an initial, so those get "U". */
  authorPhone?: string;
  currentUserId?: number | undefined;
  onUnfollow: (authorId: number) => void;
  onChangePrivacy: (postId: string, currentVisibility: string, postData: PostData) => void;
  onDelete: (postId: string) => void;
  onEdit?: (postId: string) => void;
  onShowAbout: (authorId: number) => void;
}

const PostCard: React.FC<ExtendedPostCardProps> = ({
  userAvatar,
  authorEmail,
  authorPhone,
  post,
  onReact,
  onReply,
  showFooter = true,
  currentUserId,
  onUnfollow,
  onChangePrivacy,
  onDelete,
  onEdit,
  onShowAbout,
}) => {
  const [menuListOpen, setMenuListOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  const menuButtonRef = useRef<HTMLIonButtonElement>(null);

  const handleReact = useCallback(
    (reaction: ReactionType) => {
      onReact?.(post.id, reaction);
    },
    [onReact, post.id]
  );

  useEffect(() => {
    if (!menuListOpen || !menuButtonRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            setMenuListOpen(false);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(menuButtonRef.current);

    return () => {
      observer.disconnect();
    };
  }, [menuListOpen]);

  useEffect(() => {
    if (!menuListOpen) return;

    const handleScroll = () => {
      if (menuButtonRef.current) {
        const rect = menuButtonRef.current.getBoundingClientRect();
        const inView =
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <=
          (window.innerWidth || document.documentElement.clientWidth);
        if (!inView) {
          setMenuListOpen(false);
        } else {
          setMenuPosition({
            top: rect.height + 50,
            right: 0,
          });
        }
      } else {
        setMenuListOpen(false);
      }
    };

    const handleClick = (e: MouseEvent) => {
      // Don't close if clicking the menu button itself (it has its own toggle)
      if (
        menuButtonRef.current &&
        !menuButtonRef.current.contains(e.target as Node)
      ) {
        setMenuListOpen(false);
      }
    };

    // Register event listeners
    document.addEventListener("click", handleClick);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("click", handleClick);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [menuListOpen]);

  const handleReply = useCallback(() => {
    onReply?.(post.id);
  }, [onReply, post.id]);

  const isCurrentUserPost = useMemo(
    () => currentUserId && post.authorId === +currentUserId,
    [currentUserId, post.authorId]
  );

  const handleMenuButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    if (menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.height + 5,
        right: 0,
      });
    }
    // Toggle menu instead of just opening
    setMenuListOpen((prev) => !prev);
  }, []);

  const handleMenuItemClick = useCallback(
    (itemId: string) => {
      setMenuListOpen(false);

      switch (itemId) {
        case "unfollow":
          if (post.authorId) onUnfollow(post.authorId);
          break;
        case "about":
          if (post.authorId) onShowAbout(post.authorId);
          break;
        case "privacy":
          onChangePrivacy(post.id, post.visibility, post);
          break;
        case "delete":
          onDelete(post.id);
          break;
        case "edit":
          onEdit?.(post.id);
          break;
        default:
          break;
      }
    },
    [
      post.authorId,
      post.id,
      post.visibility,
      onUnfollow,
      onShowAbout,
      onChangePrivacy,
      onDelete,
      onEdit,
    ]
  );

  const menuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [];

    if (isCurrentUserPost) {
      items.push(
        {
          id: "privacy",
          label: "Change Privacy",
          icon: eyeOutline,
          onClick: () => handleMenuItemClick("privacy"),
        },
        {
          id: "edit",
          label: "Edit Post",
          icon: pencilOutline,
          color: "danger",
          onClick: () => handleMenuItemClick("edit"),
        },
        {
          id: "delete",
          label: "Delete Post",
          icon: trashBinOutline,
          color: "danger",
          onClick: () => handleMenuItemClick("delete"),
        }
      );
    } else {
      items.push(
        {
          id: "unfollow",
          label: "Unfollow",
          icon: personRemoveOutline,
          onClick: () => handleMenuItemClick("unfollow"),
        },
        {
          id: "about",
          label: "About this person",
          icon: personCircleOutline,
          onClick: () => handleMenuItemClick("about"),
        }
      );
    }

    return items;
  }, [isCurrentUserPost, handleMenuItemClick]);

  const getMoodIcon = useCallback((moodLabel: string) => {
    const mood = MOODS.find(
      (m) => m.label.toLowerCase() === moodLabel.toLowerCase()
    );
    return mood?.icon;
  }, []);

  // One source for the author's label, initials and image.
  const authorProfile = useMemo(
    () =>
      resolveProfileDisplay({
        name: post?.userName,
        email: authorEmail,
        phone: authorPhone,
        avatar: userAvatar,
      }),
    [post?.userName, authorEmail, authorPhone, userAvatar]
  );

  return (
    <IonCard className="post-card" style={{ overflow: "visible" }}>
      <IonCardContent className="post-card-content">
        {/* Header Section */}
        <div className="post-header" style={{ position: "relative" }}>
          <div className="users-info">
            {/* <IonAvatar className="post-author-avatar !bg-transparent flex items-center justify-center bg-gray-300 text-sm font-semibold">
              {userAvatar && !imgError ? (
                <img
                  src={userAvatar}
                  className="w-8 h-8 md:h-12 md:w-12 object-cover !bg-transparent"
                  onError={() => setImgError(true)}
                  alt={post?.userName || "User"}
                />
              ) : (
                <div className="w-8 h-8 bg-[#007C9E] rounded flex items-center justify-center text-white text-sm">
                  {userInitial}
                </div>
              )}
            </IonAvatar> */}
            <Avatar
              initials={authorProfile.initials}
              imageSrc={authorProfile.avatar}
              imageClassName="rounded-[12px] border"
              innerClassName="avatar-inner-38"
            />
            <div className="user-details">
              <span
                className="users-name text-base truncate block"
                style={{ maxWidth: "150px" }}
                title={authorProfile.name}
              >
                {authorProfile.name}
              </span>
              <p className="followers">
                <IonIcon
                  className="follower-icon"
                  icon={post.visibility === "private" ? eyeOffOutline : people}
                />
                <span className="follower-label">
                  {post.visibility === "private" ? "Private" : "Following"}
                </span>
              </p>
            </div>
          </div>

          {menuItems.length > 0 && (
            <IonButton
              fill="clear"
              className="menu-button"
              ref={menuButtonRef}
              onClick={handleMenuButtonClick}
            >
              <IonIcon icon={ellipsisVerticalOutline} />
            </IonButton>
          )}
        </div>

        {/* Options Menu */}
        {menuListOpen && menuItems.length > 0 && (
          <OptionsMenu
            post={post}
            menuItems={menuItems}
            isOpen={menuListOpen}
            onDidDismiss={() => setMenuListOpen(false)}
            menuPosition={menuPosition}
            handleMenuItemClick={handleMenuItemClick}
          />
        )}

        {/* Mood indicator if present */}
        {post?.mood && (
          <div className="mood-indicator">
            <span className="mood-label-icon">
              Feeling{" "}
              {(() => {
                const MoodIcon = getMoodIcon(post.mood);
                return MoodIcon ? (
                  <MoodIcon
                    className="mood-icon w-5 h-5 inline-block mx-1"
                    aria-label={post.mood}
                  />
                ) : null;
              })()}
              {post.mood}
            </span>
          </div>
        )}

        {/* Post Content */}
        <div className="post-content">
          <IonText>
            <p>{post.content}</p>
          </IonText>
          
          {post.images && post.images.length > 0 && (
            <AuthenticatedImage
              className="post-image"
              images={post.images}
            />
          )}

          {/* Therapy Data if present */}
          {post.therapyData && post.therapyData.therapies && (
            <BiWazeTherapyCard
              title="Weekly Summary"
              currentMonth={new Date().toLocaleString("default", {
                month: "long",
              })}
              therapies={post.therapyData.therapies}
              hideFooter={true}
              timePeriod={
                post.therapyData.period === "weekly" ? "Week" : undefined
              }
            />
          )}
        </div>
      </IonCardContent>
      {showFooter && (
        <PostEngagement
          reactions={
            post.reactions ?? {
              thumbsUp: 0,
              love: 0,
              insightful: 0,
              clap: 0,
              smile: 0,
              fire: 0,
            }
          }
          replies={post.replies}
          userReaction={post.userReaction}
          timestamp={post.timestamp}
          onReact={handleReact}
          onReply={handleReply}
        />
      )}
    </IonCard>
  );
};

export default PostCard;
